import { useEffect, useState } from "react";
import {
  Modal,
  Tabs,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Text,
  Select,
  Avatar,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { addUserSchema } from "src/validations";
import { UserManagement } from "src/interfaces";
import { SubjectType } from "src/interfaces/KnowledgeBase";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import AvatarSelectionModal from "../AvatarSelectionModal";
import { AGE_OFFSET, getSchoolsByLevel } from "src/utils/constants";
import { UserPlusIcon, AcademicCapIcon } from "@heroicons/react/24/outline";



interface Props {
  opened: boolean;
  onClose: () => void;
  parents: UserManagement[];
  onCreateParent: (
    name: string,
    email: string,
    password: string,
    phone?: string
  ) => Promise<void> | void;
  onCreateLearner: (
    name: string,
    username: string,
    password: string,
    level: number,
    subjectIds: number[],
    school: string,
    dateOfBirth: string,
    parentEmail: string,
    avatarPath?: string
  ) => Promise<void> | void;
}

const AddUserModal = ({
  opened,
  onClose,
  parents,
  onCreateParent,
  onCreateLearner,
}: Props) => {
  const [active, setActive] = useState<string | null>("parent");
  const [levels, setLevels] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [availableSubjects, setAvailableSubjects] = useState<
    Array<SubjectType>
  >([]);
  const [schools, setSchools] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string>("");
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  useEffect(() => {
    if (!opened) {
      // Reset avatar when modal closes
      setSelectedAvatar("");
      return;
    }
    // fetch levels
    const load = async () => {
      const resp = await knowledgeBaseService.getLevels();
      if (resp.kind === "ok" && resp.data) {
        setLevels(
          resp.data.map((l) => ({ value: String(l.id), label: l.name }))
        );
      }

      // reset subjects, schools and avatar
      setAvailableSubjects([]);
      setSchools([]);
      setSelectedAvatar("");
    };
    load();
  }, [opened]);

  const form = useForm({
    initialValues: {
      mode: "parent",

      // parent
      parentFirstName: "",
      parentLastName: "",
      parentEmail: "",
      parentPhone: "",
      parentPassword: "",

      // learner
      firstName: "",
      lastName: "",
      username: "",
      password: "",
      level: "",
      parent_id: "",
      subjects: [] as string[],
      year: "",
      school: "",
    },
    validate: yupResolver(addUserSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  // sync tab mode with form
  useEffect(() => {
    form.setFieldValue("mode", active ?? "parent");
  }, [active]);

  // when level changes, fetch subjects for that level and update schools
  useEffect(() => {
    const loadSubjects = async () => {
      const llevel = form.values.level;
      if (!llevel) {
        setAvailableSubjects([]);
        setSchools([]);
        form.setFieldValue("subjects", []);
        form.setFieldValue("school", "");
        return;
      }

      // Get level name and update schools
      const selectedLevel = levels.find((level) => level.value === llevel);
      const levelName = selectedLevel?.label || "";
      const schoolsList = getSchoolsByLevel(levelName);
      setSchools(schoolsList);

      const levelId = Number(llevel);
      const resp = await knowledgeBaseService.getSubjects(levelId);
      if (resp.kind === "ok" && resp.data) {
        setAvailableSubjects(resp.data);
        // keep any previously selected that still exist
        const availableNames = resp.data.map((s) => s.name);
        let newSelection = (form.values.subjects || []).filter((p) => availableNames.includes(p));

        // Enforce required subjects
        const requiredSubjects = resp.data.filter((s) => s.is_required);
        requiredSubjects.forEach((req) => {
          if (!newSelection.includes(req.name)) {
            newSelection.push(req.name);
          }
        });

        form.setFieldValue("subjects", newSelection);
      } else {
        setAvailableSubjects([]);
        form.setFieldValue("subjects", []);
      }
    };
    loadSubjects();
  }, [form.values.level]);

  const submit = form.onSubmit(
    async (values) => {
      console.log("Form submitted with values:", values);
      setLoading(true);
      try {
        if (values.mode === "parent") {
          const fullName =
            `${values.parentFirstName} ${values.parentLastName}`.trim();
          await onCreateParent(
            fullName,
            values.parentEmail,
            values.parentPassword,
            values.parentPhone || undefined
          );
          // Success - reset form and close modal
          form.reset();
          setActive("parent");
          setAvailableSubjects([]);
          setSelectedAvatar("");
          onClose();
        } else {
          const fullName = `${values.firstName} ${values.lastName}`.trim();

          // Convert subject names to IDs
          const subjectIds = availableSubjects
            .filter((sub) => values.subjects.includes(sub.name))
            .map((sub) => sub.id);

          // Format date of birth (year field to YYYY-01-01)
          const dateOfBirth = values.year ? `${values.year}-01-01` : "";

          // Get parent email from selected parent
          const selectedParent = parents.find(
            (p) => String(p.id) === values.parent_id
          );
          const parentEmail = selectedParent?.email || "";

          await onCreateLearner(
            fullName,
            values.username,
            values.password,
            Number(values.level),
            subjectIds,
            values.school,
            dateOfBirth,
            parentEmail,
            selectedAvatar || undefined
          );
          // Success - reset form and close modal
          form.reset();
          setActive("parent");
          setAvailableSubjects([]);
          setSelectedAvatar("");
          onClose();
        }
      } catch (e) {
        // Error - keep modal open with user data
        console.error("Error creating user:", e);
      } finally {
        setLoading(false);
      }
    },
    (validationErrors) => {
      console.log("Form validation errors:", validationErrors);
    }
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Group position="apart" noWrap>
            <Group spacing="sm" noWrap>
              <Avatar radius="xl" color="blue" size={40}>
                {active === "parent" ? (
                  <UserPlusIcon className="w-5 h-5" />
                ) : (
                  <AcademicCapIcon className="w-5 h-5" />
                )}
              </Avatar>
              <div>
                <Title order={4} style={{ marginBottom: 2 }}>
                  {active === "parent" ? "Add Parent" : "Add Learner"}
                </Title>
                <Text size="xs" color="dimmed">
                  {active === "parent"
                    ? "Create a new parent account"
                    : "Create a new learner and assign to a parent"}
                </Text>
              </div>
            </Group>
          </Group>
        </div>
      }
      centered
      size={700}
      zIndex={2200}
      styles={{
        body: {
          height: '60vh',
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        },
      }}
    >
      <Tabs
        value={active}
        onTabChange={(v) => setActive(v)}
        styles={{
          root: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          panel: {
            flex: 1,
            overflow: "auto",
            padding: "16px 16px 0px",
          },
        }}
      >
        <Tabs.List
          className="grid grid-cols-2 gap-2"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "white",
            padding: "16px 16px 8px 16px",
            margin: 0,
          }}
        >
          <Tabs.Tab value="parent" className="w-full text-center">
            <Group position="center" spacing={6}>
              <UserPlusIcon className="w-4 h-4 text-blue-600" />
              <span>Parent</span>
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="learner" className="w-full text-center">
            <Group position="center" spacing={6}>
              <AcademicCapIcon className="w-4 h-4 text-indigo-600" />
              <span>Learner</span>
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="parent" pt="xs">
          <form onSubmit={submit}>
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  label="First name"
                  placeholder="First name"
                  {...form.getInputProps("parentFirstName")}
                />
                <TextInput
                  label="Last name"
                  placeholder="Last name"
                  {...form.getInputProps("parentLastName")}
                />
              </div>
              <TextInput
                label="Email"
                placeholder="Enter email"
                {...form.getInputProps("parentEmail")}
              />
              <TextInput
                label="Phone"
                placeholder="Enter phone number"
                {...form.getInputProps("parentPhone")}
              />

              <PasswordInput
                label="Password"
                placeholder="Enter password"
                {...form.getInputProps("parentPassword")}
              />
              <Group
                position="right"
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "white",
                  padding: "16px 0",
                  marginTop: "16px",
                  borderTop: "1px solid #e9ecef",
                }}
              >
                <Button variant="default" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  color="dark"
                  type="submit"
                  loading={loading}
                  leftIcon={<UserPlusIcon className="w-4 h-4" />}
                >
                  Create Parent
                </Button>
              </Group>
            </div>
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="learner" pt="xs">
          <form onSubmit={submit}>
            <div className="flex flex-col gap-3">
              <div>
                <Select
                  label="Parent"
                  placeholder="Select parent"
                  data={parents.map((p) => ({
                    value: String(p.id),
                    label: p.name,
                  }))}
                  value={form.values.parent_id || undefined}
                  onChange={(v) => form.setFieldValue("parent_id", v || "")}
                  searchable
                  required
                />
                {form.errors.parent_id && (
                  <Text color="red">{String(form.errors.parent_id)}</Text>
                )}
              </div>

              {/* If no parent selected, hide the rest of the learner form */}
              {!form.values.parent_id ? (
                <Text color="dimmed" size="sm">
                  Select a parent to continue filling the learner details
                </Text>
              ) : (
                <>
                  {/* Profile Picture Selection */}
                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    <UnstyledButton onClick={() => setAvatarModalOpen(true)}>
                      <Avatar
                        src={selectedAvatar || null}
                        size={80}
                        radius="xl"
                        style={{ cursor: "pointer", border: "2px dashed #ccc" }}
                      />
                    </UnstyledButton>
                    <div className="flex flex-col gap-2">
                      <Text size="sm" fw={600}>
                        Profile Picture
                      </Text>
                      {selectedAvatar ? (
                        <div className="flex gap-2">
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => setAvatarModalOpen(true)}
                          >
                            Change
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            color="red"
                            onClick={() => setSelectedAvatar("")}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <Text size="xs" color="dimmed">
                          Click the icon to select an avatar
                        </Text>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <TextInput
                      label="First name"
                      placeholder="First"
                      {...form.getInputProps("firstName")}
                    />
                    <TextInput
                      label="Last name"
                      placeholder="Last"
                      {...form.getInputProps("lastName")}
                    />
                  </div>

                  <div>
                    <Select
                      label="Level"
                      placeholder="Select level"
                      data={levels}
                      value={form.values.level || undefined}
                      onChange={(v) => form.setFieldValue("level", v || "")}
                      searchable
                    />
                    {form.errors.level && (
                      <Text color="red">{String(form.errors.level)}</Text>
                    )}
                  </div>

                  {/* Only show year of birth after level is selected */}
                  {form.values.level && (
                    <div>
                      <Select
                        label="Year of birth"
                        placeholder="Select year"
                        data={(() => {
                          const selectedLevel = levels.find(
                            (level) => level.value === form.values.level
                          );
                          const levelName = selectedLevel?.label || "";
                          const age = AGE_OFFSET[levelName] || 12;
                          const currentYear = new Date().getFullYear();

                          return [0, -1, -2].map((offset) => ({
                            value: (currentYear - age + offset).toString(),
                            label: (currentYear - age + offset).toString(),
                          }));
                        })()}
                        {...form.getInputProps("year")}
                        searchable
                      />
                    </div>
                  )}

                  {/* Only show subjects after a level is selected */}
                  {form.values.level ? (
                    <div>
                      <Text size="sm" fw={600} mb={8}>
                        Subjects
                      </Text>
                      <div className="flex flex-wrap gap-3">
                        {availableSubjects.map((sub) => (
                          <div key={sub.id} className="flex items-center gap-1">
                            <input
                              id={`sub-${sub.id}`}
                              type="checkbox"
                              checked={(form.values.subjects || []).includes(
                                sub.name
                              )}
                              onChange={() => {
                                // If required, do not allow deselecting
                                if (sub.is_required) return;

                                const cur = new Set(form.values.subjects || []);
                                if (cur.has(sub.name)) cur.delete(sub.name);
                                else cur.add(sub.name);
                                form.setFieldValue("subjects", Array.from(cur));
                              }}
                              disabled={sub.is_required}
                            />
                            <label htmlFor={`sub-${sub.id}`}>{sub.name}</label>
                          </div>
                        ))}
                      </div>
                      {form.errors.subjects && (
                        <Text color="red">{String(form.errors.subjects)}</Text>
                      )}
                    </div>
                  ) : null}

                  {/* Only show school dropdown after a level is selected */}
                  {form.values.level && (
                    <Select
                      label="School"
                      placeholder="Select school"
                      data={schools}
                      {...form.getInputProps("school")}
                      searchable
                    />
                  )}

                  <TextInput
                    label="Username"
                    placeholder="Enter username (unique)"
                    {...form.getInputProps("username")}
                  />
                  <PasswordInput
                    label="Password"
                    placeholder="Enter password"
                    {...form.getInputProps("password")}
                  />


                  <Group
                    position="right"
                    style={{
                      position: "sticky",
                      bottom: 0,
                      background: "white",
                      padding: "16px 0",
                      marginTop: "16px",
                      borderTop: "1px solid #e9ecef",
                    }}
                  >
                    <Button
                      variant="default"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="dark"
                      type="submit"
                      loading={loading}
                      leftIcon={<AcademicCapIcon className="w-4 h-4" />}
                    >
                      Create Learner
                    </Button>
                  </Group>
                </>
              )}
            </div>
          </form>
        </Tabs.Panel>
      </Tabs>

      <AvatarSelectionModal
        opened={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onSelect={setSelectedAvatar}
        currentAvatar={selectedAvatar}
      />
    </Modal>
  );
};

export default AddUserModal;
